/**
 * ThesisWorkbenchPane — Engineer-mode 5-step Thesis Design Wizard.
 *
 * State machine from core/thesis_designer.py exposed via
 * /api/v1/engineer/theses/wizard/*. Each step has hard gates that
 * match the server; we let the server enforce (409 Conflict → surface
 * as error) and keep the client thin.
 *
 * Step flow:
 * 1. Pick driver (from ranked uncovered drivers)
 * 2. Socratic polish: statement + ≥3 success + ≥2 failure + ≥1 invalidation
 * 3. Feasibility verdict (green/yellow/red)
 * 4. ThesisSet + correlation preview
 * 5. Contrarian review (mandatory)
 * Commit → creates Thesis + thesis_positions; queues buy orders as
 *          pending_approval for tomorrow's Morning Brief.
 */
import { useEffect, useState } from 'react';
import * as api from '../lib/api';
import { useFocal } from '../lib/focal';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../lib/toast';
import { SwarmActivity } from './SwarmActivity';

type Archetype =
  | 'compounder'
  | 'nascent_growth'
  | 'disruptor'
  | 'mature_cash_flow'
  | 'cyclical'
  | 'leveraged_recovery'
  | 'special_situation';

const ARCHETYPES: Archetype[] = [
  'compounder',
  'nascent_growth',
  'disruptor',
  'mature_cash_flow',
  'cyclical',
  'leveraged_recovery',
  'special_situation',
];

export function ThesisWorkbenchPane({ wizardId: wizardIdProp }: { wizardId?: string }) {
  const { set: _setFocal } = useFocal();
  const navigate = useNavigate();
  const toast = useToast();
  const [state, setState] = useState<api.WizardState | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // On mount: start new wizard OR load existing
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        if (wizardIdProp) {
          const s = await api.engineer.wizardGet(wizardIdProp);
          if (alive) setState(s);
        } else {
          const s = await api.engineer.wizardStart({ top_k_uncovered: 6 });
          if (alive) setState(s);
        }
      } catch (e) {
        if (alive) setErr(e instanceof Error ? e.message : String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [wizardIdProp]);

  // v0.5.9 AE-113 — live refresh: the Concierge can advance this wizard
  // from chat (thesis_wizard_* MCP tools), so lightly poll the draft while
  // it's open and swap in fresh state when updated_at moves. Committed
  // wizards (step 6) stop polling; a 404 (committed/discarded elsewhere)
  // stops it too via the interval's silent catch.
  const wizardId = state?.wizard_id;
  const wizardDone = (state?.current_step ?? 0) >= 6;
  useEffect(() => {
    if (!wizardId || wizardDone) return;
    const t = setInterval(async () => {
      try {
        const fresh = await api.engineer.wizardGet(wizardId);
        setState((cur) =>
          cur && cur.wizard_id === fresh.wizard_id && fresh.updated_at !== cur.updated_at
            ? fresh
            : cur,
        );
      } catch {
        // draft may have been committed or discarded from another surface
      }
    }, 4000);
    return () => clearInterval(t);
  }, [wizardId, wizardDone]);

  if (loading) return <div className="text-sm text-ink-mute">loading wizard…</div>;
  if (err || !state) {
    return (
      <div className="card p-3 border-warn text-xs">
        error: {err ?? 'no wizard state'}
      </div>
    );
  }

  return (
    <div className="lg:grid lg:grid-cols-[1fr_260px] lg:gap-3 lg:items-start space-y-3 lg:space-y-0">
      <div className="space-y-3">
      <StepHeader current={state.current_step} />

      {state.current_step === 1 && (
        <Step1 state={state} onAdvance={setState} onError={setErr} />
      )}
      {state.current_step === 2 && (
        <Step2 state={state} onAdvance={setState} onError={setErr} />
      )}
      {state.current_step === 3 && (
        <Step3 state={state} onAdvance={setState} onError={setErr} />
      )}
      {state.current_step === 4 && (
        <Step4 state={state} onAdvance={setState} onError={setErr} />
      )}
      {state.current_step === 5 && (
        <Step5
          state={state}
          onAdvance={setState}
          onCommit={(result) => {
            const thesisId = (result as { thesis_id?: string }).thesis_id;
            // v0.5.5 AE-081: replace fire-and-forget toast with a sticky
            // "View in Portfolio" CTA so the operator can land on their
            // newly published thesis instead of being thrown back to /today.
            toast.show(result.message, 'ok', {
              action: thesisId
                ? {
                    label: 'View in Portfolio',
                    onClick: () => navigate(`/portfolio?view=theses&thesis_id=${thesisId}`),
                  }
                : undefined,
            });
            navigate('/today');
          }}
          onError={setErr}
        />
      )}

      {err && (
        <div className="card p-3 border-warn text-xs">error: {err}</div>
      )}

      <div className="text-[11px] text-ink-mute">
        wizard_id: <span className="num">{state.wizard_id}</span> · Analytical artifacts
        commit immediately; real-money orders route to the Auto Morning Brief for approval.
      </div>
      </div>

      {/* v0.5.9 AE-111: watch the swarm work while you author */}
      <div className="lg:sticky lg:top-3 mt-3 lg:mt-0">
        <SwarmActivity title="the floor" />
      </div>
    </div>
  );
}

function StepHeader({ current }: { current: number }) {
  const labels = [
    'driver',
    'refine',
    'feasibility',
    'thesis set',
    'contrarian',
  ];
  return (
    <div className="flex items-center gap-1 text-xs">
      {labels.map((label, i) => {
        const step = i + 1;
        const active = step === current;
        const done = step < current;
        return (
          <div key={label} className="flex-1 flex items-center gap-1">
            <span
              className={`pill text-[10px] ${
                active
                  ? 'border-accent text-accent'
                  : done
                    ? 'text-accent'
                    : 'text-ink-mute'
              }`}
            >
              {step}. {label}
            </span>
            {i < labels.length - 1 && (
              <span className="flex-1 border-t border-edge" />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ===========================================================================
// Step 1 — pick driver
// ===========================================================================

function Step1({
  state,
  onAdvance,
  onError,
}: {
  state: api.WizardState;
  onAdvance: (s: api.WizardState) => void;
  onError: (e: string | null) => void;
}) {
  const [picking, setPicking] = useState<string | null>(null);
  const options = state.step1.ranked_options ?? [];

  async function pick(driver: string) {
    setPicking(driver);
    onError(null);
    try {
      const next = await api.engineer.wizardStep1(state.wizard_id, driver);
      onAdvance(next);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setPicking(null);
    }
  }

  return (
    <div className="card p-3">
      <div className="text-xs text-ink-mute mb-2">
        Which macro driver does this thesis lean into? (Ranked by how much
        diversification benefit it adds to your current portfolio.)
      </div>
      <div className="space-y-2">
        {options.length === 0 && (
          <div className="text-xs text-ink-mute">
            No uncovered drivers ranked. Portfolio already diversified —
            consider a new thesis only if conviction is strong.
          </div>
        )}
        {options.map((opt) => (
          <button
            key={opt.driver}
            onClick={() => pick(opt.driver)}
            disabled={picking !== null}
            className="w-full text-left card p-2 hover:border-accent disabled:opacity-50"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm">{opt.label}</div>
                <div className="text-[11px] text-ink-mute mt-0.5 break-words" style={{ overflowWrap: 'anywhere' }}>
                  {opt.rationale}
                </div>
              </div>
              <div className="text-xs num text-ink-mute whitespace-nowrap shrink-0">
                +{opt.diversification_score.toFixed(2)}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ===========================================================================
// Step 2 — refine statement + criteria
// ===========================================================================

function Step2({
  state,
  onAdvance,
  onError,
}: {
  state: api.WizardState;
  onAdvance: (s: api.WizardState) => void;
  onError: (e: string | null) => void;
}) {
  const [statement, setStatement] = useState(state.step2.polished_statement);
  const [success, setSuccess] = useState<string[]>(
    state.step2.success_criteria.length >= 3 ? state.step2.success_criteria : ['', '', ''],
  );
  const [failure, setFailure] = useState<string[]>(
    state.step2.failure_criteria.length >= 2 ? state.step2.failure_criteria : ['', ''],
  );
  const [invalid, setInvalid] = useState<string[]>(
    state.step2.invalidation_triggers.length >= 1 ? state.step2.invalidation_triggers : [''],
  );
  const [horizon, setHorizon] = useState<number>(state.step2.horizon_days || 730);
  const [archetype, setArchetype] = useState<Archetype>(
    (state.step2.archetype as Archetype) || 'compounder',
  );
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    const cleanSuccess = success.map((s) => s.trim()).filter(Boolean);
    const cleanFailure = failure.map((s) => s.trim()).filter(Boolean);
    const cleanInvalid = invalid.map((s) => s.trim()).filter(Boolean);
    if (cleanSuccess.length < 3) {
      onError('need ≥ 3 success criteria');
      return;
    }
    if (cleanFailure.length < 2) {
      onError('need ≥ 2 failure criteria');
      return;
    }
    if (cleanInvalid.length < 1) {
      onError('need ≥ 1 invalidation trigger');
      return;
    }
    if (!statement.trim()) {
      onError('statement cannot be empty');
      return;
    }
    setSubmitting(true);
    onError(null);
    try {
      const next = await api.engineer.wizardStep2(state.wizard_id, {
        polished_statement: statement,
        success_criteria: cleanSuccess,
        failure_criteria: cleanFailure,
        invalidation_triggers: cleanInvalid,
        horizon_days: horizon,
        archetype,
      });
      onAdvance(next);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="card p-3 space-y-3">
      <div>
        <div className="text-xs text-ink-mute mb-1">Thesis statement</div>
        <textarea
          className="w-full bg-bg-elev border border-edge rounded p-2 text-xs"
          rows={3}
          placeholder="One to three sentences. What's the insight?"
          value={statement}
          onChange={(e) => setStatement(e.target.value)}
        />
      </div>

      <CriteriaList
        label="Success criteria (≥ 3 — what has to be true for this to work?)"
        values={success}
        setValues={setSuccess}
        minRows={3}
      />
      <CriteriaList
        label="Failure criteria (≥ 2 — what would make you sell?)"
        values={failure}
        setValues={setFailure}
        minRows={2}
      />
      <CriteriaList
        label="Invalidation triggers (≥ 1 — hard exit signals)"
        values={invalid}
        setValues={setInvalid}
        minRows={1}
      />

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-ink-mute block mb-1">Horizon (days)</label>
          <input
            type="number"
            className="w-full bg-bg-elev border border-edge rounded p-2 num text-xs"
            value={horizon}
            min={30}
            onChange={(e) => setHorizon(Number(e.target.value))}
          />
        </div>
        <div>
          <label className="text-xs text-ink-mute block mb-1">Archetype</label>
          <select
            className="w-full bg-bg-elev border border-edge rounded p-2 text-xs"
            value={archetype}
            onChange={(e) => setArchetype(e.target.value as Archetype)}
          >
            {ARCHETYPES.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={submit} disabled={submitting} className="btn text-xs">
          {submitting ? 'saving…' : 'next: feasibility →'}
        </button>
      </div>
    </div>
  );
}

function CriteriaList({
  label,
  values,
  setValues,
  minRows,
}: {
  label: string;
  values: string[];
  setValues: (v: string[]) => void;
  minRows: number;
}) {
  function update(i: number, v: string) {
    const copy = [...values];
    copy[i] = v;
    setValues(copy);
  }
  function add() {
    setValues([...values, '']);
  }
  function remove(i: number) {
    if (values.length <= minRows) return;
    const copy = [...values];
    copy.splice(i, 1);
    setValues(copy);
  }
  return (
    <div>
      <div className="text-xs text-ink-mute mb-1">{label}</div>
      <div className="space-y-1">
        {values.map((v, i) => (
          <div key={i} className="flex gap-1">
            <input
              className="flex-1 bg-bg-elev border border-edge rounded p-2 text-xs"
              value={v}
              onChange={(e) => update(i, e.target.value)}
            />
            {values.length > minRows && (
              <button
                type="button"
                onClick={() => remove(i)}
                className="btn text-xs"
                aria-label="remove"
              >
                −
              </button>
            )}
          </div>
        ))}
        <button type="button" onClick={add} className="btn text-xs w-full">
          + add
        </button>
      </div>
    </div>
  );
}

// ===========================================================================
// Step 3 — feasibility
// ===========================================================================

function Step3({
  state,
  onAdvance,
  onError,
}: {
  state: api.WizardState;
  onAdvance: (s: api.WizardState) => void;
  onError: (e: string | null) => void;
}) {
  const [weight, setWeight] = useState<number>(
    state.step3.target_satellite_weight_pct || 0.15,
  );
  const [blendedTarget, setBlendedTarget] = useState<number>(
    state.step3.blended_cagr_target || 0.15,
  );
  const [existingContrib, setExistingContrib] = useState<number>(
    state.step3.existing_contribution_cagr || 0.10,
  );
  const [satelliteOfNav, setSatelliteOfNav] = useState<number>(
    state.step3.satellite_weight_of_nav || 0.40,
  );
  const [submitting, setSubmitting] = useState(false);

  const verdict = state.step3.verdict;

  async function compute() {
    setSubmitting(true);
    onError(null);
    try {
      const next = await api.engineer.wizardStep3(state.wizard_id, {
        target_satellite_weight_pct: weight,
        blended_cagr_target: blendedTarget,
        existing_contribution_cagr: existingContrib,
        satellite_weight_of_nav: satelliteOfNav,
      });
      onAdvance(next);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="card p-3 space-y-3">
      <div className="text-xs text-ink-mute">
        Given your blended CAGR target + current portfolio contribution,
        what CAGR does this thesis need to hit for your target to work?
        The Historical Validator checks if that's empirically achievable
        for the archetype + horizon.
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs">
        <NumberRow
          label="target satellite weight (% of satellite sleeve)"
          value={weight}
          onChange={setWeight}
          step={0.01}
          suffix="%"
          mul={100}
        />
        <NumberRow
          label="blended CAGR target (overall)"
          value={blendedTarget}
          onChange={setBlendedTarget}
          step={0.01}
          suffix="%"
          mul={100}
        />
        <NumberRow
          label="existing contribution CAGR"
          value={existingContrib}
          onChange={setExistingContrib}
          step={0.01}
          suffix="%"
          mul={100}
        />
        <NumberRow
          label="satellite weight of NAV"
          value={satelliteOfNav}
          onChange={setSatelliteOfNav}
          step={0.01}
          suffix="%"
          mul={100}
        />
      </div>

      <button
        onClick={compute}
        disabled={submitting}
        className="btn text-xs w-full"
      >
        {submitting ? 'computing…' : 'compute feasibility →'}
      </button>

      {verdict && (
        <div
          className={`card p-3 border-2 ${
            verdict === 'green'
              ? 'border-accent text-accent'
              : verdict === 'yellow'
                ? 'border-edge'
                : 'border-warn text-warn'
          }`}
        >
          <div className="font-bold text-sm uppercase">{verdict}</div>
          <div className="text-xs mt-1">{state.step3.verdict_explanation}</div>
          <div className="text-[11px] text-ink-mute mt-2 num">
            required CAGR: {(state.step3.required_cagr_value * 100).toFixed(1)}% ·
            p95 historical: {(state.step3.historical_p95_cagr * 100).toFixed(1)}%
          </div>
        </div>
      )}
    </div>
  );
}

function NumberRow({
  label,
  value,
  onChange,
  step,
  suffix,
  mul = 1,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
  step: number;
  suffix?: string;
  mul?: number;
}) {
  return (
    <div>
      <label className="text-ink-mute block mb-1">{label}</label>
      <div className="flex items-center gap-1">
        <input
          type="number"
          className="flex-1 bg-bg-elev border border-edge rounded p-2 num"
          value={(value * mul).toFixed(2)}
          step={step * mul}
          onChange={(e) => onChange(Number(e.target.value) / mul)}
        />
        {suffix && <span className="text-ink-mute num">{suffix}</span>}
      </div>
    </div>
  );
}

// ===========================================================================
// Step 4 — ThesisSet + correlation
// ===========================================================================

function Step4({
  state,
  onAdvance,
  onError,
}: {
  state: api.WizardState;
  onAdvance: (s: api.WizardState) => void;
  onError: (e: string | null) => void;
}) {
  const [symbols, setSymbols] = useState<string>(
    state.step4.selected_symbols.join(', '),
  );
  const [submitting, setSubmitting] = useState(false);
  const [asking, setAsking] = useState(false);

  // v0.5.9 AE-112 — "Ask the desks": kick off the thesis pane's A2A
  // research pipeline. The SwarmActivity strip alongside shows every
  // dispatch live; results land as selectable candidate chips below.
  async function askDesks() {
    setAsking(true);
    onError(null);
    try {
      const next = await api.engineer.wizardStep4Suggest(state.wizard_id);
      onAdvance(next);
    } catch (e) {
      onError(formatDesksError(e));
    } finally {
      setAsking(false);
    }
  }

  function toggleCandidate(sym: string) {
    const list = symbols
      .split(',')
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);
    const next = list.includes(sym)
      ? list.filter((s) => s !== sym)
      : [...list, sym];
    setSymbols(next.join(', '));
  }

  async function submit() {
    const list = symbols
      .split(',')
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);
    if (list.length === 0) {
      onError('at least one symbol required');
      return;
    }
    setSubmitting(true);
    onError(null);
    try {
      const next = await api.engineer.wizardStep4(state.wizard_id, {
        selected_symbols: list,
      });
      onAdvance(next);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  const parsedSymbols = symbols
    .split(',')
    .map((s) => s.trim().toUpperCase())
    .filter(Boolean);

  const candidates = (state.step4.candidates ?? []).filter(
    (c): c is Record<string, unknown> & { symbol: string } =>
      typeof (c as { symbol?: unknown }).symbol === 'string',
  );

  return (
    <div className="card p-3 space-y-3">
      <div className="text-xs text-ink-mute">
        Select the basket of securities that makes up this thesis's ThesisSet.
        Pairwise correlation must stay ≤ 0.70 (a concentration-cap guard).
      </div>

      {/* v0.5.9 AE-112 — desk-suggested candidates */}
      <div className="space-y-2">
        <button
          type="button"
          data-testid="ask-desks"
          onClick={askDesks}
          disabled={asking || submitting}
          className="btn text-xs w-full"
          title="Runs the Thesis pane's research pipeline (universe → macro → theme momentum → fundamentals screen → sentiment) via live A2A dispatches"
        >
          {asking ? 'the desks are working…' : 'ask the desks for candidates'}
        </button>
        {candidates.length > 0 && (
          <div data-testid="desk-candidates" className="space-y-1">
            <div className="text-[11px] text-ink-mute">
              Desk-ranked candidates — tap to add/remove. You choose; the desks only rank.
            </div>
            <div className="flex flex-wrap gap-1">
              {candidates.map((c) => {
                const sym = c.symbol as string;
                const selected = parsedSymbols.includes(sym);
                const sent = c.sentiment_score;
                return (
                  <button
                    key={sym}
                    type="button"
                    data-testid={`candidate-${sym}`}
                    data-selected={selected ? 'true' : 'false'}
                    onClick={() => toggleCandidate(sym)}
                    className={`pill text-[11px] num ${
                      selected ? 'border-accent text-accent' : 'text-ink-mute'
                    }`}
                  >
                    {typeof c.rank === 'number' ? `#${c.rank} ` : ''}
                    {sym}
                    {typeof sent === 'number' ? ` · s ${sent.toFixed(2)}` : ''}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div>
        <label className="text-xs text-ink-mute block mb-1">
          Symbols (comma-separated)
        </label>
        <input
          className="w-full bg-bg-elev border border-edge rounded p-2 num text-sm"
          placeholder="NVDA, AVGO, TSM"
          value={symbols}
          onChange={(e) => setSymbols(e.target.value)}
        />
      </div>
      {parsedSymbols.length > 0 && (
        <TechnicalZonesPanel symbols={parsedSymbols} />
      )}
      <div className="flex justify-end">
        <button onClick={submit} disabled={submitting} className="btn text-xs">
          {submitting ? 'checking…' : 'next: contrarian →'}
        </button>
      </div>
    </div>
  );
}

/**
 * v0.5.9 AE-112: surface the honest 503 from the desk-assist routes
 * ("desks_unavailable") as a readable message instead of raw JSON.
 * Exported for the wizard-desks vitest.
 */
export function formatDesksError(err: unknown): string {
  if (err instanceof api.ApiError) {
    const d = err.detail;
    if (
      d &&
      typeof d === 'object' &&
      'detail' in d &&
      typeof (d as { detail: unknown }).detail === 'object' &&
      (d as { detail: { reason?: string } }).detail?.reason === 'desks_unavailable'
    ) {
      const inner = (d as { detail: { error?: string } }).detail;
      return `the desks are unavailable: ${inner.error ?? 'A2A pipeline failed'}`;
    }
    if (
      d &&
      typeof d === 'object' &&
      'detail' in d &&
      typeof (d as { detail: unknown }).detail === 'string'
    ) {
      return (d as { detail: string }).detail;
    }
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

/**
 * AE-010 (UX-WIZARD-02): convert the typed `position_cap_breach` 409
 * payload into an operator-readable message that names the symbol,
 * the proposed weight, and the Playbook cap. Falls back to the
 * server's `message` (or the raw error) for any other shape.
 *
 * Exported so the wizard's vitest in
 * src/test/wizard-conviction-cap.test.tsx can assert the formatting
 * without re-mounting the whole workbench.
 */
export function formatCommitError(err: unknown): string {
  if (err instanceof api.ApiError) {
    const d = err.detail;
    if (
      d &&
      typeof d === 'object' &&
      'detail' in d &&
      typeof (d as { detail: unknown }).detail === 'object' &&
      (d as { detail: { code?: string } }).detail?.code === 'position_cap_breach'
    ) {
      const cap = (d as { detail: any }).detail;
      const pct = (cap.position_pct * 100).toFixed(1);
      const capPct = (cap.cap_pct * 100).toFixed(0);
      return (
        `Playbook cap: ${cap.symbol} would be ${pct}% of NAV ` +
        `(\$${cap.position_usd.toFixed(0)} of \$${cap.nav_usd.toFixed(0)}); ` +
        `max per position is ${capPct}%. ` +
        `Reduce satellite exposure, add diversification symbols, or revise the thesis size.`
      );
    }
    if (
      d &&
      typeof d === 'object' &&
      'detail' in d &&
      typeof (d as { detail: unknown }).detail === 'string'
    ) {
      return (d as { detail: string }).detail;
    }
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

/**
 * AE-010 (UX-WIZARD-01): inline technical-zones panel for Step 4.
 *
 * Calls POST /api/v1/research/technical-zones for each selected symbol.
 * Renders support / resistance / current price / entry zone when bars
 * are available. When the broker can't supply enough history, surfaces
 * the `reason` verbatim and does NOT fabricate a zone (anti-hallucination
 * Layer 4).
 *
 * Exported so the vitest in src/test/wizard-technical-zones.test.tsx
 * can mount it directly.
 */
export function TechnicalZonesPanel({ symbols }: { symbols: string[] }) {
  type State =
    | { kind: 'loading' }
    | { kind: 'ok'; data: api.TechnicalZonesResponse }
    | { kind: 'error'; message: string };

  const [zones, setZones] = useState<Record<string, State>>({});

  useEffect(() => {
    let cancelled = false;
    setZones(Object.fromEntries(symbols.map((s) => [s, { kind: 'loading' as const }])));
    Promise.all(
      symbols.map((sym) =>
        api.research.technicalZones({ symbol: sym })
          .then<[string, State]>((data) => [sym, { kind: 'ok', data }])
          .catch<[string, State]>((e) => [
            sym,
            { kind: 'error', message: e instanceof Error ? e.message : String(e) },
          ]),
      ),
    ).then((results) => {
      if (cancelled) return;
      setZones(Object.fromEntries(results));
    });
    return () => {
      cancelled = true;
    };
  }, [symbols.join(',')]);

  return (
    <div data-testid="technical-zones-panel" className="space-y-1">
      <div className="text-xs text-ink-mute">
        Technical zones (support / resistance / entry classification)
      </div>
      <div className="grid grid-cols-1 gap-1">
        {symbols.map((sym) => (
          <TechnicalZoneRow key={sym} symbol={sym} state={zones[sym]} />
        ))}
      </div>
    </div>
  );
}

function TechnicalZoneRow({
  symbol,
  state,
}: {
  symbol: string;
  state: { kind: 'loading' } | { kind: 'ok'; data: api.TechnicalZonesResponse } | { kind: 'error'; message: string } | undefined;
}) {
  if (!state || state.kind === 'loading') {
    return (
      <div
        data-testid={`tz-row-${symbol}`}
        data-status="loading"
        className="card p-2 text-xs flex items-center justify-between"
      >
        <span className="num">{symbol}</span>
        <span className="text-ink-mute">loading…</span>
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <div
        data-testid={`tz-row-${symbol}`}
        data-status="error"
        className="card p-2 text-xs flex items-center justify-between border-warn"
      >
        <span className="num">{symbol}</span>
        <span className="text-warn">network: {state.message}</span>
      </div>
    );
  }
  const d = state.data;
  if (!d.available) {
    return (
      <div
        data-testid={`tz-row-${symbol}`}
        data-status="unavailable"
        className="card p-2 text-xs flex items-center justify-between"
      >
        <span className="num">{symbol}</span>
        <span className="text-ink-mute">no zone — {d.reason}</span>
      </div>
    );
  }
  const zoneTone =
    d.entry_zone === 'buy_zone'
      ? 'text-accent border-accent'
      : d.entry_zone === 'sell_zone'
        ? 'text-warn border-warn'
        : 'text-ink-mute border-edge';
  return (
    <div
      data-testid={`tz-row-${symbol}`}
      data-status="ok"
      className="card p-2 text-xs grid grid-cols-5 gap-2 items-center"
    >
      <span className="num font-semibold">{symbol}</span>
      <span className="num">S ${d.support.toFixed(2)}</span>
      <span className="num">@ ${d.current_price.toFixed(2)}</span>
      <span className="num">R ${d.resistance.toFixed(2)}</span>
      <span className={`pill text-[10px] ${zoneTone}`}>
        {d.entry_zone.replace('_', ' ')} · conf {d.confidence.toFixed(2)}
      </span>
    </div>
  );
}

// ===========================================================================
// Step 5 — Contrarian review + commit
// ===========================================================================

function Step5({
  state,
  onAdvance,
  onCommit,
  onError,
}: {
  state: api.WizardState;
  onAdvance: (s: api.WizardState) => void;
  onCommit: (result: api.WizardCommitResponse) => void;
  onError: (e: string | null) => void;
}) {
  const [review, setReview] = useState<string>(
    state.step5.contrarian_review_md,
  );
  const [submitting, setSubmitting] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [fetchingTake, setFetchingTake] = useState(false);

  // v0.5.9 AE-112 — the Contrarian PANE's take, side-by-side reference.
  // It never satisfies the commit gate; the user still writes their own.
  async function fetchTake() {
    setFetchingTake(true);
    onError(null);
    try {
      const next = await api.engineer.wizardStep5Contrarian(state.wizard_id);
      onAdvance(next);
    } catch (e) {
      onError(formatDesksError(e));
    } finally {
      setFetchingTake(false);
    }
  }

  async function saveReview() {
    if (!review.trim()) {
      onError('contrarian review cannot be empty');
      return;
    }
    setSubmitting(true);
    onError(null);
    try {
      const next = await api.engineer.wizardStep5(state.wizard_id, review);
      onAdvance(next);
    } catch (e) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function commit() {
    if (!review.trim()) {
      onError('save contrarian review before committing');
      return;
    }
    setCommitting(true);
    onError(null);
    try {
      await api.engineer.wizardStep5(state.wizard_id, review);
      const result = await api.engineer.wizardCommit(state.wizard_id);
      onCommit(result);
    } catch (e) {
      onError(formatCommitError(e));
      setCommitting(false);
    }
  }

  return (
    <div className="card p-3 space-y-3">
      <div className="text-xs text-ink-mute">
        <b className="text-warn">Mandatory:</b> write a steelman of the
        opposing case. What would make this thesis WRONG? Which smart
        contrarian would bet against it and why? (This review is stored
        and shown every time this thesis is reviewed.)
      </div>

      {/* v0.5.9 AE-112 — the Contrarian pane's reference take */}
      <div className="space-y-1">
        <button
          type="button"
          data-testid="ask-contrarian"
          onClick={fetchTake}
          disabled={fetchingTake || submitting || committing}
          className="btn text-xs w-full"
          title="A2A call to the Contrarian pane. Reference only — your own steelman below still gates commit."
        >
          {fetchingTake ? 'the Contrarian is thinking…' : "get the Contrarian's take"}
        </button>
        {state.step5.pane_take_md && (
          <div
            data-testid="contrarian-take"
            className="card p-2 text-xs whitespace-pre-wrap border-edge"
          >
            <div className="text-[10px] uppercase text-ink-mute mb-1">
              the Contrarian's take (reference — write your own steelman below)
            </div>
            {state.step5.pane_take_md}
          </div>
        )}
      </div>

      <textarea
        className="w-full bg-bg-elev border border-edge rounded p-2 text-xs"
        rows={12}
        placeholder="The strongest case against this thesis is…"
        value={review}
        onChange={(e) => setReview(e.target.value)}
      />
      <div className="flex items-center justify-between">
        <div className="text-xs text-ink-mute">
          {review.length} chars
          {state.step5.commit_approved && (
            <span className="ml-2 text-accent">· review saved</span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={saveReview}
            disabled={submitting || committing}
            className="btn text-xs"
          >
            {submitting ? 'saving…' : 'save review'}
          </button>
          <button
            onClick={commit}
            disabled={committing}
            className="btn text-xs border-accent text-accent"
            title="creates Thesis + ThesisSet; queues buy orders as pending_approval for tomorrow's Morning Brief"
          >
            {committing ? 'committing…' : 'commit thesis →'}
          </button>
        </div>
      </div>
    </div>
  );
}
